import { useQuery, gql } from '@apollo/client';
import { LandsbankinnContext } from '@landsbankinn/utils';
import React, { useEffect, useState, useContext } from 'react';
import 'core-js/proposals/string-match-all';

export const queries = {
  fee: gql`
    query singleFee($id: String!) {
      node(id: $id) {
        ... on Fee {
          id
          description
          type
          value
          reference
        }
      }
    }
  `,
  interest: gql`
    query singleInterestRate($id: String!) {
      node(id: $id) {
        ... on InterestRate {
          id
          type
          value
          previousValue
          description
          name
        }
      }
    }
  `,
};

export function formatValue(value: any, type: any, fallback: string) {
  if (typeof value === 'undefined' || value === null) {
    return fallback;
  }
  if (type === 'Percentage') {
    return Intl.NumberFormat('it-IT', {
      style: 'percent',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value / 100);
  }
  if (type === 'Amount') {
    return `${Intl.NumberFormat('it-IT').format(value)} kr.`;
  }
  return value;
}

export const InlineExpression = ({
  type,
  index,
}: {
  type: 'fee' | 'interest';
  index: string;
}) => {
  const fallback = `-`;

  const { client } = useContext(LandsbankinnContext);

  const id = Buffer.from(
    type === 'interest' ? `InterestRate:${index}` : `Fee:${index}`,
  ).toString('base64');

  const res = useQuery(queries[type], {
    client,
    skip: !queries[type],
    variables: { id },
  });

  if (!queries[type]) {
    return <span>{fallback}</span>;
  }

  const value = formatValue(
    res.data?.node?.value,
    type === 'interest' ? 'Percentage' : res.data?.node?.type,
    fallback,
  );

  return <span>{res.loading ? '...' : value}</span>;
};

export const useInlineExpressions = (str?: string) => {
  const [output, setOutput] = useState(str);
  const { client } = useContext(LandsbankinnContext);

  useEffect(() => {
    if (str) {
      const tariffs = [...str.matchAll(/%(fee|interest)(\d+)%/g)];
      Promise.all(
        tariffs.map(async (tariff) => {
          const [_, type, index] = tariff;
          const fallback = `-`;
          const id = Buffer.from(
            type === 'interest' ? `InterestRate:${index}` : `Fee:${index}`,
          ).toString('base64');
          const res = await client.query({
            query: queries[type],
            variables: { id },
          });
          return {
            type,
            index,
            value: formatValue(
              res.data?.node?.value,
              type === 'interest' ? 'Percentage' : res.data?.node?.type,
              fallback,
            ),
          };
        }),
      ).then((results) => {
        const replaced = str.replace(
          /%(fee|interest)(\d+)%/g,
          (original, type, index) => {
            return (
              results.find((res) => res.type === type && res.index === index)
                ?.value ?? original
            );
          },
        );
        setOutput(replaced);
      });
    }
  }, [str, client]);

  if (!str) {
    return null;
  }

  return output;
};
